<template>
  <div class="commission-view">
    <div class="container-fluid">
      <div class="row mb-4">
        <div class="col-12">
          <h1 class="mb-3">Sales Commission Dashboard</h1>
          <p class="text-muted">
            Manage sales partners and track referral commissions.
            <b>THIS PAGE DOES NOT TAKE INTO ACCOUNT FORWARD SALES</b>
          </p>
        </div>
      </div>

      <TabsContainer
        :tabs="tabs"
        :activeTab="activeTab ?? 'partners'"
        @change:tab="setTab"
      >
        <template #tab-partners>
          <!-- Dashboard Stats -->
          <div class="row mb-4">
            <div class="col-md-3">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <h5 class="card-title text-muted mb-2">Total Referrers</h5>
                  <h2 class="mb-0">{{ dashboardData.length }}</h2>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <h5 class="card-title text-muted mb-2">
                    Total Referred Companies
                  </h5>
                  <h2 class="mb-0">{{ totalReferredCompanies }}</h2>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <h5 class="card-title text-muted mb-2">Total Power (kW)</h5>
                  <h2 class="mb-0">{{ formatNumber(totalPower) }}</h2>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <h5 class="card-title text-muted mb-2">Projected MWh/Year</h5>
                  <h2 class="mb-0">
                    {{ formatNumber(totalProjectedProduction) }}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <!-- Referrers Table -->
          <div class="row">
            <div class="col-12">
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-white">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <h5 class="mb-0">Sales Partners & Referrers</h5>
                    <div class="input-group search-input">
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Search..."
                        v-model="searchQuery"
                      />
                    </div>
                  </div>
                </div>
                <div class="table-responsive fixed-height-table">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="table-light sticky-top bg-light">
                      <tr>
                        <th @click="sortBy('company_name')" class="sortable">
                          Company Name
                          <i
                            v-if="sortKey === 'company_name'"
                            :class="sortIconClass"
                          ></i>
                        </th>
                        <th
                          @click="sortBy('sales_partner_type')"
                          class="sortable"
                        >
                          Partner type
                          <i
                            v-if="sortKey === 'sales_partner_type'"
                            :class="sortIconClass"
                          ></i>
                        </th>
                        <th
                          @click="sortBy('referred_companies_count')"
                          class="sortable"
                        >
                          Referred Companies
                          <i
                            v-if="sortKey === 'referred_companies_count'"
                            :class="sortIconClass"
                          ></i>
                        </th>
                        <th @click="sortBy('total_power_kw')" class="sortable">
                          Total Power (kW)
                          <i
                            v-if="sortKey === 'total_power_kw'"
                            :class="sortIconClass"
                          ></i>
                        </th>
                        <th
                          @click="sortBy('projected_production_mwh')"
                          class="sortable"
                        >
                          Projected MWh/Year
                          <i
                            v-if="sortKey === 'projected_production_mwh'"
                            :class="sortIconClass"
                          ></i>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="referrer in filteredAndSortedReferrers"
                        :key="referrer.company_id"
                      >
                        <td>{{ referrer.company_name }}</td>
                        <td>
                          <span
                            :class="getPartnerBadgeClass(referrer)"
                            class="badge rounded-pill"
                          >
                            {{ getPartnerTypeLabel(referrer) }}
                          </span>
                        </td>
                        <td>{{ referrer.referred_companies_count }}</td>
                        <td>{{ formatNumber(referrer.total_power_kw) }}</td>
                        <td>
                          {{ formatNumber(referrer.projected_production_mwh) }}
                        </td>
                        <td>
                          <div class="btn-group">
                            <button
                              class="btn btn-sm btn-outline-primary"
                              @click="
                                viewReferredCompanies(referrer.company_id)
                              "
                            >
                              View Companies
                            </button>
                            <EditCommissionButton
                              :referrer="referrer"
                              :managerCandidates
                              :accountData="{
                                admin_account_id:
                                  accountData.admin_account_id ?? null,
                              }"
                              @updated="fetchDashboardData"
                            />
                          </div>
                        </td>
                      </tr>
                      <tr v-if="filteredAndSortedReferrers.length === 0">
                        <td colspan="6" class="text-center py-4">
                          <p class="text-muted mb-0">No referrers found</p>
                        </td>
                      </tr>
                      <!-- Spacer row for extra padding at the bottom -->
                      <tr class="table-spacer">
                        <td colspan="6"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #tab-booked>
          <BookedCommissionsReport :referrerOptions="referrerOptions" />
        </template>

        <template #tab-revenue>
          <SalesPartnerRevenueReport />
        </template>
      </TabsContainer>

      <!-- Referred Companies Modal Component -->
      <ReferredCompaniesModal
        :companyId="selectedCompanyId"
        :isVisible="showReferredCompaniesModal"
        :accountData="{
          admin_account_id: accountData.admin_account_id ?? null,
        }"
        @close="closeReferredCompaniesModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import BookedCommissionsReport from "@/admin/commission/BookedCommissionsReport.vue";
import ReferredCompaniesModal from "@/admin/commission/ReferredCompaniesModal.vue";
import SalesPartnerRevenueReport from "@/admin/commission/SalesPartnerRevenueReport.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useAccount } from "@/composables/account/useAccount";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Tab } from "@/composables/useTabs";
import { useSwapReferrers } from "@/composables/useSwapReferrers";
import { useTabs } from "@/composables/useTabs";
import EditCommissionButton from "@/money/admin/commission/EditCommissionButton.vue";
import { Modal } from "bootstrap";
import { orderBy, sumBy } from "lodash-es";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";

// Get global error handler
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

// Managers are picked from the same sales partners that can be swap referrers
const { options: managerCandidates, fetchCandidates: fetchManagerCandidates } =
  useSwapReferrers();

// Get account data
const { accountData } = useAccount();

const tabs: Tab[] = [
  { id: "partners", name: "Sales Partners", icon: "user-tie" },
  { id: "booked", name: "Booked commissions", icon: "file-invoice-dollar" },
  { id: "revenue", name: "Revenue by partner", icon: "chart-column" },
];
const { activeTab, setTab } = useTabs("admin-commission", {
  tabs,
  defaultTabId: "partners",
});

const modalInstance = ref<Modal | null>(null);
const dashboardData = ref<any[]>([]);
const searchQuery = ref("");
const sortKey = ref("sales_partner_type");
const sortDirection = ref<"asc" | "desc">("desc");
const selectedCompanyId = ref<number | null>(null);
const showReferredCompaniesModal = ref(false);

const referrerOptions = computed(() =>
  dashboardData.value.map((referrer) => ({
    label: referrer.company_name,
    value: referrer.company_id,
  }))
);

// Computed properties
const totalReferredCompanies = computed(() =>
  sumBy(dashboardData.value, "referred_companies_count")
);

const totalPower = computed(() => sumBy(dashboardData.value, "total_power_kw"));

const totalProjectedProduction = computed(() =>
  sumBy(dashboardData.value, "projected_production_mwh")
);

const filteredAndSortedReferrers = computed(() => {
  const query = searchQuery.value.toLowerCase();

  return orderBy(
    dashboardData.value.filter((referrer) => {
      return (
        !query ||
        referrer.company_name.toLowerCase().includes(query) ||
        (referrer.sales_partner_type || "").toLowerCase().includes(query)
      );
    }),
    [(referrer) => referrer[sortKey.value] ?? ""],
    [sortDirection.value]
  );
});

const sortIconClass = computed(() => {
  return sortDirection.value === "asc" ? "bi bi-sort-up" : "bi bi-sort-down";
});

// Methods
const fetchDashboardData = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/commission/dashboard");
    dashboardData.value = response.data.data;
  } catch (error) {
    globalErrorHandler(error);
  }
};

const viewReferredCompanies = (companyId: number) => {
  selectedCompanyId.value = companyId;
  showReferredCompaniesModal.value = true;

  // Initialize the Bootstrap modal
  const modalElement = document.getElementById("referredCompaniesModal");
  if (modalElement) {
    modalInstance.value = new Modal(modalElement);
    modalInstance.value.show();
  }
};

const closeReferredCompaniesModal = () => {
  showReferredCompaniesModal.value = false;
  if (modalInstance.value) {
    modalInstance.value.dispose();
    modalInstance.value = null;
  }
};

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortDirection.value = "asc";
  }
};

const getPartnerBadgeClass = (referrer: any) => {
  if (referrer.sales_partner_type) {
    return "bg-primary";
  } else {
    return "bg-secondary";
  }
};

const getPartnerTypeLabel = (referrer: any) => {
  if (referrer.sales_partner_type) {
    return referrer.sales_partner_type;
  } else {
    return "Referrer";
  }
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat().format(value);
};

// Lifecycle hooks
onMounted(async () => {
  await Promise.all([fetchDashboardData(), fetchManagerCandidates()]);
});

// Clean up on component unmount
onUnmounted(() => {
  if (modalInstance.value) {
    modalInstance.value.dispose();
    modalInstance.value = null;
  }
});
</script>

<style scoped lang="scss">
.commission-view {
  padding: 20px 0;
}

.fixed-height-table {
  height: 500px;
  overflow-y: auto;
  display: block;
}

.table {
  width: 100%;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Bottom spacer for the table */
.table-spacer td {
  height: 50px;
  border-bottom: none;
}

/* Search input width */
.search-input {
  width: 300px;
}
</style>
